<template>
	<v-card class="mx-auto" elevation="0">
		<v-card-title class="card-header fixed-card-title2">
			{{ $t('add-location-mapping') }}
			<v-spacer />
			<v-icon @click="closeDrawer" class="white-icon mr-3">mdi-close</v-icon>
		</v-card-title>
		<v-card-text class="pt-4">
			<v-row dense>
				<v-col cols="12">
					<v-select
						class="py-1"
						hide-details
						dense
						v-model="locationToAdd.mappedMedicalInstitutionId"
						:items="allMedicalInstitutions"
						item-value="id"
						item-text="institutionName"
						:label="$t('organizations')"
						outlined
						:menu-props="{ offsetY: true }" />
				</v-col>
				<v-col cols="12">
					<v-select
						:items="notMappedLocationsFiltered"
						item-text="hospitalName"
						item-value="id"
						outlined
						hide-details
						dense
						:label="`${$t('location')}`"
						v-model="locationToAdd.mappedHospitalId"
						:menu-props="{ offsetY: true }" />
				</v-col>
				<v-col>
					<v-divider />
				</v-col>
				<v-col cols="12">
					<v-text-field
						outlined
						dense
						hide-details="auto"
						v-model="locationToAdd.ecpLocationName"
						:label="`${$t('ecp-location-name')}`"
						required />
				</v-col>

				<v-col cols="12">
					<v-text-field
						outlined
						dense
						hide-details="auto"
						v-model="locationToAdd.ecpLocationId"
						:label="`${$t('location')} GUID`"
						required />
				</v-col>

				<v-col>
					<v-divider />
				</v-col>

				<v-col cols="12">
					<v-select
						outlined
						dense
						class="expand-selection"
						:items="apiKeyOptions"
						hide-details="auto"
						v-model="locationToAdd.apiKey"
						:label="`${$t('api-key')}`"
						required
						:menu-props="{ offsetY: true }" />
				</v-col>

				<v-col cols="12" v-if="locationToAdd.apiKey === 'custom'">
					<v-text-field
						outlined
						dense
						hide-details="auto"
						v-model="locationToAdd.customApiKey"
						:label="`${$t('custom-api-key')}`" />
				</v-col>

				<v-col cols="12">
					<v-text-field
						outlined
						dense
						hide-details="auto"
						v-model="locationToAdd.clientId"
						:label="`${$t('client-id')}`"
						required />
				</v-col>

				<v-col cols="12">
					<v-textarea
						rows="2"
						no-resize
						outlined
						dense
						hide-details="auto"
						v-model="locationToAdd.clientSecret"
						:label="`${$t('client-secret')}`"
						required />
				</v-col>

				<v-col>
					<v-divider />
				</v-col>

				<v-col cols="12">
					<v-checkbox
						outlined
						dense
						hide-details="auto"
						v-model="locationToAdd.finalizeMeasurementAutomatically"
						:label="`${$t('finalize-measurement-automatically')}`" />
				</v-col>

				<v-col cols="12">
					<v-checkbox
						outlined
						dense
						hide-details="auto"
						v-model="locationToAdd.residentCreationIsAutomatic"
						:label="`${$t('resident-creation-is-automatic')}`" />
				</v-col>

				<v-col cols="12">
					<v-checkbox
						outlined
						dense
						hide-details="auto"
						v-model="locationToAdd.userCreationIsAutomatic"
						:label="`${$t('user-creation-is-automatic')}`" />
				</v-col>
			</v-row>
		</v-card-text>
		<v-container class="fixed-card-actions">
			<v-row dense>
				<v-col cols="6">
					<v-btn elevation="0" @click="closeDrawer" class="cancel-button-style capitalize-text">
						{{ $t('cancel') }}
					</v-btn>
				</v-col>
				<v-col cols="6">
					<v-btn :disabled="isSaveButtonDisabled()" @click="saveOrganizationMapping" class="save-button-style">
						{{ $t('save') }}
					</v-btn>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import MappingRepository from '../../data/repositories/v1/MappingRepository';

export default {
	name: 'OrganizationMappingAdd',
	data: () => ({
		componentKey: 0,
		menu: false,
		defaultTimeZone: 'America/Chicago',
		selectedMedicalInstitution: null,
		ecpLocationGuid: '',
		ecpLocationName: '',
		apiKey: '',
		clientId: '',
		clientServer: '',
		notMappedLocations: [],
		notMappedLocationsFiltered: [],

		locationToAdd: {
			ecpLocationId: '',
			mappedHospitalId: 0,
			mappedMedicalInstitutionId: 0,
			ecpLocationName: '',
			finalizeMeasurementAutomatically: true,
			residentCreationIsAutomatic: false,
			userCreationIsAutomatic: false,
			apiKey: 'E5D18F2B-E932-922D-893D462BE7CF8CF1',
			customApiKey: '',
			clientId: '',
			clientSecret: '',
		},
	}),
	computed: {
		...mapState({
			allMedicalInstitutions: (state) => state.medicalInstitutions.medicalInstitutions,
			hospitals: (state) => state.hospitals.hospitals,
		}),
		...mapGetters({
			getTimeZone: 'verifiers/getTimeZone',
			ecpLocations: 'mapping/getEcpLocation',
		}),
		apiKeyOptions () {
			return [
				{ text: 'PROD - E5D18F2B-E932-922D-893D462BE7CF8CF1', value: 'E5D18F2B-E932-922D-893D462BE7CF8CF1' },
				{ text: 'TEST - 03B4ABB2-9E26-13B9-474ABEF09DC75E8B', value: '03B4ABB2-9E26-13B9-474ABEF09DC75E8B' },
				{ text: this.$t('custom'), value: 'custom' },
			];
		},
	},
	mounted () {
		this.getMedicalInstitutions();
		this.getEcpNotMappedLocation();
	},
	methods: {
		...mapActions({
			getMedicalInstitutions: 'medicalInstitutions/getMedicalInstitutions',
		}),
		isSaveButtonDisabled () {
			return (
				!this.locationToAdd.ecpLocationId.length ||
				this.locationToAdd.mappedHospitalId === 0 ||
				this.locationToAdd.mappedMedicalInstitutionId === 0 ||
				!this.locationToAdd.ecpLocationName.length ||
				(this.locationToAdd.apiKey === 'custom' && !this.locationToAdd.customApiKey.length) ||
				!this.locationToAdd.clientId.length ||
				!this.locationToAdd.clientSecret.length
			);
		},
		resetComponent () {
			this.selectedMedicalInstitution = null;
			this.ecpLocationGuid = '';
			this.ecpLocationName = '';
			this.locationToAdd = {
				ecpLocationId: '',
				mappedHospitalId: 0,
				mappedMedicalInstitutionId: 0,
				ecpLocationName: '',
				finalizeMeasurementAutomatically: true,
				residentCreationIsAutomatic: false,
				userCreationIsAutomatic: false,
				apiKey: 'E5D18F2B-E932-922D-893D462BE7CF8CF1',
				customApiKey: '',
				clientId: '',
				clientSecret: '',
			};
		},
		async saveOrganizationMapping () {
			const body = {
				...this.locationToAdd,
				apiKey: this.locationToAdd.apiKey === 'custom' ? this.locationToAdd.customApiKey : this.locationToAdd.apiKey,
			};

			MappingRepository.addEcpMapForLocation(body).then((res) => {
				this.closeDrawer();
			});
		},
		closeDrawer () {
			this.resetComponent();
			this.$store.commit('SET_DETAILS_DRAWER', false);
		},
		getEcpNotMappedLocation () {
			MappingRepository.getNotMappedEcpLocation().then((res) => {
				this.notMappedLocations = res;
			});
		},
	},
	watch: {
		'locationToAdd.mappedMedicalInstitutionId' (val) {
			this.notMappedLocationsFiltered = this.notMappedLocations.filter((n) => n.medicalInstitutionId === val);
		},
	},
};
</script>
<style scoped>
.map-container {
	margin-top: 20px;
	/* Adjust this margin as needed */
}
.expand-selection :deep(.v-select__selection) {
	max-width: 100%;
}
</style>
